<template>
  <div
    ref="editorRoot"
    class="relative editor-root"
    :dir="isRTLView ? 'rtl' : 'ltr'"
  >
    <tag-agents
      v-if="showUserMentions && isPrivate"
      :search-key="mentionSearchKey"
      :fixed-position="true"
      @click="insertMentionNode"
    />
    <canned-response
      v-if="shouldShowCannedResponses"
      :search-key="cannedSearchTerm"
      @click="insertCannedResponse"
    />
    <variable-list
      v-if="shouldShowVariables"
      :search-key="variableSearchTerm"
      @click="insertVariable"
    />
    <input
      ref="imageUpload"
      type="file"
      accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
      hidden
      @change="onFileChange"
    />
    <div
      ref="editor"
      class="relative editor-container"
      :class="{
        'hide-toolbar': !showToolbarInternal,
        'typing-direction-rtl': typingDirection === 'rtl',
        'typing-direction-ltr': typingDirection === 'ltr',
      }"
    />
    <slot name="warning-message" />
    <div
      v-show="isImageNodeSelected && showImageResizeToolbar"
      class="absolute shadow-md rounded-[4px] flex gap-1 py-1 px-1 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-50"
      :style="{
        top: toolbarPosition.top,
        left: toolbarPosition.left,
      }"
    >
      <button
        v-for="size in sizes"
        :key="size.name"
        class="text-xs font-medium rounded-[4px] border border-solid border-slate-200 dark:border-slate-600 px-1.5 py-0.5 hover:bg-slate-100 dark:hover:bg-slate-800"
        @click="setURLWithQueryAndImageSize(size)"
      >
        {{ size.name }}
      </button>
    </div>
    <div class="message-toolbar">
      <div
        v-if="hasAttachments"
        id="editor-attachments-tray"
        class="flex flex-col pt-2 overflow-y-auto custom-scroll max-h-[148px] border-t border-gray-200"
      >
        <editor-attachments-tray
          class="[&>.preview-item]:dark:bg-slate-700 flex-row flex-wrap gap-x-3 gap-y-1"
          :attachments="attachedFiles"
          :remove-attachment="removeAttachment"
        />
      </div>
      <div class="flex flex-row items-center gap-[10px] pt-2">
        <div class="relative flex">
          <span
            ref="emojiTrigger"
            v-tooltip.top="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
            :title="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
            class="material-symbols-outlined message-toolbar-item"
            @click.stop="toggleEmojiPicker"
          >
            sentiment_satisfied
          </span>
          <emoji-input
            v-show="showEmojiPicker"
            v-on-clickaway="onClickAwayEmojiPicker"
            :class="emojiDialogClassOnRTLView"
            :on-click="emojiOnClick"
            :trigger-el="$refs.emojiTrigger"
            :visible="showEmojiPicker"
          />
        </div>
        <span
          v-tooltip.top="$t('CONVERSATION.REPLYBOX.TIP_FORMATTING_TOOLBAR')"
          class="material-symbols-outlined message-toolbar-item"
          :class="{ active: showToolbarInternal }"
          @click="toggleEditorToolbar"
        >
          custom_typography
        </span>
        <span
          v-if="typingDirection === 'rtl'"
          v-tooltip.top="$t('CONVERSATION.REPLYBOX.TIP_TEXT_DIRECTION')"
          class="material-symbols-outlined message-toolbar-item"
          @click="switchTypingDirection('ltr')"
        >
          format_textdirection_l_to_r
        </span>
        <span
          v-else
          v-tooltip.top="$t('CONVERSATION.REPLYBOX.TIP_TEXT_DIRECTION')"
          class="material-symbols-outlined message-toolbar-item"
          @click="switchTypingDirection('rtl')"
        >
          format_textdirection_r_to_l
        </span>
        <slot name="toolbar-left" />
        <div class="flex-1" />
        <slot name="toolbar-right" />
      </div>
    </div>
  </div>
</template>

<script>
import { setBlockType } from 'prosemirror-commands';
import { mixin as clickaway } from 'vue-clickaway';
import Editor from './Editor.vue';
import {
  buildEditor,
  EditorState,
  createMessageSchema,
  MessageMarkdownTransformer,
  MessageMarkdownSerializer,
} from './EmailEditor/config';
import inboxMixin, { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import rtlMixin from 'shared/mixins/rtlMixin';
import EditorAttachmentTray from 'dashboard/components/widgets/WootWriter/EditorAttachmentTray.vue';
import { replaceVariablesInMessage } from '@chatwoot/utils';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

const EmojiInput = () => import('shared/components/emoji/EmojiInput');

const createState = (
  content,
  placeholder,
  // eslint-disable-next-line default-param-last
  plugins = [],
  // eslint-disable-next-line default-param-last
  methods = {},
  enabledMenuOptions,
  defaultDirection = 'ltr'
) => {
  const schema = createMessageSchema(defaultDirection);
  return EditorState.create({
    doc: new MessageMarkdownTransformer(schema).parse(content),
    plugins: buildEditor({
      schema,
      placeholder,
      methods,
      plugins,
      enabledMenuOptions,
      defaultDirection,
      skipLayoutMenuOptions: true,
    }),
  });
};

export default {
  name: 'WootMessageEditorV2',
  components: {
    EmojiInput,
    EditorAttachmentsTray: EditorAttachmentTray,
  },
  extends: Editor,
  mixins: [clickaway, inboxMixin, rtlMixin],
  props: {
    showToolbar: {
      type: Boolean,
      default: false,
    },
    inbox: {
      type: Object,
      default: () => ({}),
    },
    attachedFiles: {
      type: Array,
      default: () => [],
    },
    removeAttachment: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      showToolbarInternal: this.showToolbar,
      typingDirection: 'ltr',
      showEmojiPicker: false,
    };
  },
  computed: {
    defaultDirection() {
      return this.isRTLView ? 'rtl' : 'ltr';
    },
    contentFromEditor() {
      return MessageMarkdownSerializer.serialize(this.editorView.state.doc);
    },
    hasAttachments() {
      return this.attachedFiles.length > 0;
    },
    emojiDialogClassOnRTLView() {
      return this.isRTLView ? 'emoji-dialog--rtl' : '';
    },
  },
  created() {
    // Overwrite state from Editor's created() to use EmailEditor's buildEditor (new icons + schema)
    this.state = createState(
      this.value,
      this.placeholder,
      this.plugins,
      { onImageUpload: this.openFileBrowser },
      this.editorMenuOptions,
      this.defaultDirection
    );
  },
  mounted() {
    this.typingDirection = this.defaultDirection;
  },
  methods: {
    reloadState(content = this.value) {
      this.state = createState(
        content,
        this.placeholder,
        this.plugins,
        { onImageUpload: this.openFileBrowser },
        this.editorMenuOptions,
        this.defaultDirection
      );
      this.editorView.updateState(this.state);
      this.focusEditor(content);
    },
    insertCannedResponse(cannedItem) {
      const updatedMessage = replaceVariablesInMessage({
        message: cannedItem,
        variables: this.variables,
      });
      if (!this.editorView) return null;
      let node = new MessageMarkdownTransformer(
        this.editorView.state.schema
      ).parse(updatedMessage);
      const from =
        node.textContent === updatedMessage
          ? this.range.from
          : this.range.from - 1;
      this.insertNodeIntoEditor(node, from, this.range.to);
      this.$track(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
      return false;
    },
    insertContentIntoEditor(content, defaultFrom = 0) {
      const from = defaultFrom || this.editorView.state.selection.from || 0;
      let node = new MessageMarkdownTransformer(
        this.editorView.state.schema
      ).parse(content);
      this.insertNodeIntoEditor(node, from, undefined);
    },
    setDirection(direction) {
      if (!this.editorView) return;
      const { state, dispatch } = this.editorView;
      setBlockType(state.schema.nodes.paragraph, {
        align: direction === 'rtl' ? 'right' : 'left',
        direction,
        autoDetected: false,
      })(state, dispatch);
      this.editorView.focus();
    },
    emojiOnClick(emoji) {
      this.insertContentIntoEditor(emoji);
      this.editorView?.focus();
    },
    toggleEditorToolbar() {
      this.showToolbarInternal = !this.showToolbarInternal;
    },
    onClickAwayEmojiPicker() {
      if (this.showEmojiPicker) this.hideEmojiPicker();
    },
    switchTypingDirection(direction) {
      this.typingDirection = direction;
      this.setDirection(direction);
    },
    hideEmojiPicker() {
      this.showEmojiPicker = false;
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
  },
};
</script>

<style lang="scss" scoped>
.editor-root {
  min-height: unset;
}

.message-editor {
  @apply px-0 border-0;

  &.note-mode {
    ::v-deep {
      .ProseMirror {
        &.ProseMirror-woot-style {
          @apply bg-yellow-50;
        }
      }
    }

    .message-toolbar {
      @apply bg-yellow-50;
    }
  }
}

.editor-wrap {
  margin-bottom: 0 !important;
}

::v-deep {
  .ProseMirror {
    &.ProseMirror-woot-style {
      min-height: 100px;
      max-height: 350px;
    }

    p {
      font-size: 16px;
      margin-top: 0px;
      margin-bottom: 0px;
    }

    .empty-node[style*='text-align: right'] {
      &::before {
        right: 0px;
      }
    }
    .empty-node[style*='text-align: left'] {
      &::before {
        left: 0px;
      }
    }
  }

  .hide-toolbar {
    .ProseMirror-menubar {
      display: none;
    }
  }

  .typing-direction-rtl {
    p {
      direction: rtl;
    }

    .empty-node[style*='text-align: left'] {
      &::before {
        left: 0px;
      }
    }
  }

  .typing-direction-ltr {
    p {
      direction: ltr;
    }

    .empty-node[style*='text-align: left'] {
      &::before {
        left: 0px;
      }
    }
  }

  .prosemirror-mention-node {
    unicode-bidi: isolate;
  }

  .ProseMirror-menubar {
    position: absolute;
    left: 70px;
    bottom: 5px;
    top: unset;
    width: fit-content;
    background: #f3f4f6;
    border-radius: 8px;
    padding: 7px 8px;
    display: flex;
    align-items: center;
    border-bottom: none;

    .ProseMirror-menuitem {
      min-width: 24px;
      height: 24px;
      align-items: center;
      display: flex;
      justify-content: center;
      border-radius: 4px;

      .ProseMirror-icon {
        padding: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;

        &.ProseMirror-menu-active {
          @apply bg-gray-200;
        }

        svg {
          width: unset !important;
          height: unset !important;
          use {
            scale: 150%;
          }
        }
      }
    }

    .ProseMirror-menu-dropdown-menu {
      top: unset;
      bottom: 100%;
      max-height: 200px;
      overflow: auto;
      border-radius: 8px;
      width: 140px;
      padding: 4px;
      background: #f3f4f6;
      box-shadow:
        0 1px 3px 0 rgba(15, 24, 40, 0.1),
        0 1px 2px 0 rgba(15, 24, 40, 0.06);
      border: 0;

      .ProseMirror-menu-dropdown-item {
        @apply text-sm text-gray-600 leading-5;

        &:hover {
          @apply bg-gray-200;
        }
      }

      &.alignment-menu.menu-item-alignment-option {
        gap: 8px;

        .ProseMirror-menu-dropdown-item {
          height: 24px;
        }
      }
    }
  }

  .ProseMirror-icon.menu-item-em-option {
    margin-right: 0px;
  }
  .ProseMirror-icon.menu-item-strong-option {
    margin-right: 8px;
  }
  .ProseMirror-icon.menu-item-bulletList-option,
  .ProseMirror-icon.menu-item-link-option {
    margin-right: 4px;
  }
  .ProseMirror-icon.menu-item-orderedList-option {
    margin-right: 8px;
  }

  &[dir='rtl'] {
    .ProseMirror-menubar {
      position: absolute;
      right: 58px;
      left: 0;
    }
  }
}

.message-toolbar {
  @apply flex flex-col w-full;

  ::v-deep .message-toolbar-item {
    @apply text-[24px] text-[#6B7280] hover:bg-neutral-100 rounded-md cursor-pointer p-1 select-none;

    &.active {
      @apply bg-gray-100;
    }

    &.disabled {
      color: #dfdfdf;
      cursor: not-allowed;
      user-select: none;
    }
  }
}

.error {
  @apply text-[#B91C1C] border-[#B91C1C];

  &.message {
    @apply text-sm;
  }
}
</style>
